<script>
import ProjectSingle from './ProjectSingle.vue';
import ProjectsSectionHeader from './ProjectsSectionHeader.vue';
import projects from '../../data/projects';

export default {
	components: { ProjectSingle, ProjectsSectionHeader },
	data: () => {
		return {
			atStart: true,
			atEnd: false,
		};
	},
	computed: {
		// Archived projects are never listed here; they remain reachable only
		// by their direct /projects/:id link.
		activeProjects() {
			return projects.filter((project) => !project.archived);
		},
		// Fades the peeking edge card into the background instead of hard-
		// cropping it, but only on the side that actually has more to
		// scroll — a true start/end edge stays fully opaque.
		edgeMaskStyle() {
			const left = this.atStart ? 'black 0%' : 'transparent 0%, black 16%';
			const right = this.atEnd
				? 'black 100%'
				: 'black 84%, transparent 100%';
			const gradient = `linear-gradient(to right, ${left}, ${right})`;
			return { maskImage: gradient, WebkitMaskImage: gradient };
		},
	},
	mounted() {
		this.updateEdges();
		this._onResize = () => this.updateEdges();
		window.addEventListener('resize', this._onResize);
	},
	beforeUnmount() {
		window.removeEventListener('resize', this._onResize);
	},
	methods: {
		// Pages by ~90% of the visible width — arrows advance roughly a
		// screenful while leaving a sliver of overlap as a scan anchor.
		scroll(direction) {
			const el = this.$refs.track;
			if (!el) return;
			el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: 'smooth' });
		},
		updateEdges() {
			const el = this.$refs.track;
			if (!el) return;
			const max = el.scrollWidth - el.clientWidth;
			this.atStart = el.scrollLeft <= 4;
			this.atEnd = el.scrollLeft >= max - 4;
		},
	},
};
</script>

<template>
	<section class="pt-8 sm:pt-10">
		<ProjectsSectionHeader show-view-all>
			<template #controls>
				<div class="flex items-center gap-2">
					<button
						type="button"
						@click="scroll(-1)"
						:disabled="atStart"
						class="carousel-arrow font-mono-ui"
						aria-label="Previous projects"
					>
						&larr;
					</button>
					<button
						type="button"
						@click="scroll(1)"
						:disabled="atEnd"
						class="carousel-arrow font-mono-ui"
						aria-label="Next projects"
					>
						&rarr;
					</button>
				</div>
			</template>
		</ProjectsSectionHeader>

		<!-- Horizontal scroll-snap strip: native two-finger trackpad swipe and touch swipe both work for free; arrows just scrollBy() on top. Both edges sit at the same page gutter as the header row above — no bleed — so left and right read symmetric; the fade mask alone signals there's more to scroll. -->
		<div
			ref="track"
			tabindex="0"
			class="carousel-track flex gap-5 sm:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2"
			:style="edgeMaskStyle"
			@scroll="updateEdges"
		>
			<div
				v-for="project in activeProjects"
				:key="project.id"
				class="snap-start shrink-0 w-[82%] sm:w-[45%] lg:w-[22%]"
			>
				<ProjectSingle :project="project" />
			</div>
		</div>
	</section>
</template>

<style scoped>
.carousel-arrow {
	@apply w-9 h-9 rounded-lg border border-line text-ink-muted flex items-center justify-center text-sm transition duration-300;
}
.carousel-arrow:hover:not(:disabled) {
	@apply text-ink border-line-strong;
}
.carousel-arrow:disabled {
	@apply opacity-30 cursor-not-allowed;
}
.carousel-track {
	scrollbar-width: none;
	-ms-overflow-style: none;
}
.carousel-track::-webkit-scrollbar {
	display: none;
}
</style>
